import * as R from "ramda";
import { createSelector } from "reselect";
import { getters } from "./model";
import { getActiveAccountId } from "Portal/NewPortal/HeaderBar/selectors";
import {
  TAB_IDS,
  TABS,
  FIELD_IDS,
  FIELD_IDS_ACTIVE_ACCOUNT
} from "./constants";
const {
  CONTACTS,
  ACCOUNTS
} = require("@sessionboard/sessionboard-value-types/src/constants/standard-module-field-ids");

const COMBINED_FIELD_IDS = [
  ...Object.values(FIELD_IDS),
  ...Object.values(FIELD_IDS_ACTIVE_ACCOUNT)
];

import * as FormSelectors from "ui-kit/Form/selectors";

export const canCreateContacts = createSelector(
  () => {},
  () => {
    return true;
  }
);

export const canUpdateContacts = createSelector(
  () => {},
  () => {
    return true;
  }
);

export const canDeleteContacts = createSelector(
  () => {},
  () => {
    return true;
  }
);

/**
 * Retrieves the value to show for a specific field.
 *
 * This function checks the `updatedData` object first, then the `data` object,
 * and finally falls back to the `defaultValue` if the field is not found in the
 * previous objects.
 *
 * @param {Object} params - The parameters object.
 * @param {string} params.fieldId - The ID of the field to retrieve the value for.
 * @param {Object} params.data - The original data object.
 * @param {Object} params.updatedData - The updated data object.
 * @param {*} [params.defaultValue] - The default value to use if the field is not found in `data` or `updatedData`.
 * @returns {*} - The value to show for the specified field.
 */
export const getFieldValueToShow = ({
  fieldId,
  data,
  updatedData,
  defaultValue
}) => {
  return updatedData[fieldId] || data[fieldId] || defaultValue || null;
};

export const getEditedField = R.compose(
  field => R.prop("value", field),
  FormSelectors.getField
);

export const getEventFields = createSelector(
  getters.fields,
  getters.visibleFieldIds,
  fields => {
    const eventFields = fields.filter(field => !field.is_global);

    // // pair event fields by every 2 fields
    // const pairedFields = [];
    // for (let i = 0; i < eventFields.length; i += 2) {
    //   pairedFields.push([eventFields[i], eventFields[i + 1]].filter(f => f));
    // }

    // pair event fields by every 2 fields
    const pairedFields = [];
    let i = 0;
    const SINGLE_ROW_FIELD_TYPES = ["textarea", "wysiwyg"];
    while (i < eventFields.length) {
      if (
        SINGLE_ROW_FIELD_TYPES.includes(eventFields[i]?.type) ||
        SINGLE_ROW_FIELD_TYPES.includes(eventFields[i + 1]?.type)
      ) {
        pairedFields.push([eventFields[i]].filter(f => f));
        i += 1;
      } else {
        pairedFields.push([eventFields[i], eventFields[i + 1]].filter(f => f));
        i += 2;
      }
    }

    return pairedFields;
  }
);

export const getCustomFields = createSelector(
  getters.fields,
  getters.visibleFieldIds,
  fields => {
    const customFields = fields.filter(
      field => field.is_global && !COMBINED_FIELD_IDS.includes(field.id)
    );

    // pair event fields by every 2 fields
    const pairedFields = [];
    let i = 0;
    const SINGLE_ROW_FIELD_TYPES = ["textarea", "wysiwyg"];
    while (i < customFields.length) {
      if (
        SINGLE_ROW_FIELD_TYPES.includes(customFields[i]?.type) ||
        SINGLE_ROW_FIELD_TYPES.includes(customFields[i + 1]?.type)
      ) {
        pairedFields.push([customFields[i]].filter(f => f));
        i += 1;
      } else {
        pairedFields.push(
          [customFields[i], customFields[i + 1]].filter(f => f)
        );
        i += 2;
      }
    }

    return pairedFields;
  }
);

export const getTabs = createSelector(getEventFields, eventFields => {
  const canViewEventFields = true;

  if (!canViewEventFields || !eventFields.length || !eventFields[0].length) {
    return TABS.filter(tab => tab.id !== TAB_IDS.EVENT_INFO);
  }

  return TABS;
});

export const isViewingAsAccount = createSelector(
  getActiveAccountId,
  activeAccountId => {
    return activeAccountId ? true : false;
  }
);

export const showAdditionalContacts = createSelector(
  isViewingAsAccount,
  isViewingAsAccount => {
    return isViewingAsAccount ? false : true;
  }
);

export const showAccountContacts = createSelector(
  isViewingAsAccount,
  isViewingAsAccount => {
    return isViewingAsAccount ? true : false;
  }
);

export const showCustomFields = createSelector(
  getters.fields,
  getters.visibleFieldIds,
  () => {
    return true;
  }
);

export const isFormValid = createSelector(
  getters.updatedFieldValues,
  updatedFieldValues => {
    return Object.keys(updatedFieldValues).length > 0;
  }
);

export const getProfilePhotoValue = createSelector(
  getters.data,
  getters.updatedFieldValues,
  isViewingAsAccount,
  (data, updatedData, isViewingAsAccount) => {
    const value = getFieldValueToShow({
      fieldId: isViewingAsAccount
        ? FIELD_IDS_ACTIVE_ACCOUNT.LOGO_URL
        : FIELD_IDS.AVATAR_URL,
      data,
      updatedData,
      defaultValue: null
    });

    const decoratedValue =
      R.prop("url")(value) && R.prop("url")(value).length
        ? R.prop("url")(value)
        : R.path([0, "url"])(value)
        ? R.path([0, "url"])(value)
        : value && value.length
        ? value
        : null;

    return decoratedValue;
  }
);

export const getProfileName = createSelector(getters.bio, bio => bio.name);

export const getProfileEmail = createSelector(getters.bio, bio => bio.email);

const getOptions = (fields, fieldId) =>
  R.compose(
    R.map(o => ({
      label: o.value,
      id: o.id
    })),
    R.pathOr([], ["settings", "options"]),
    R.find(R.propEq("id", fieldId))
  )(fields);

export const getFieldOptions = createSelector(getters.fields, fields => {
  return {
    [CONTACTS.PRONOUNS]: getOptions(fields, CONTACTS.PRONOUNS),
    [CONTACTS.GENDER]: getOptions(fields, CONTACTS.GENDER),
    [CONTACTS.LANGUAGES]: getOptions(fields, CONTACTS.LANGUAGES),
    [CONTACTS.GLOBAL_REGION]: getOptions(fields, CONTACTS.GLOBAL_REGION),
    [ACCOUNTS.TIER]: getOptions(fields, ACCOUNTS.TIER)
  };
});
